import { useCallback, useEffect, useState } from 'react';
import {
  FileText,
  Presentation,
  FolderOpen,
  Link2,
  ExternalLink,
  Trash2,
  Loader2,
  Upload,
  FileOutput,
  MessageSquare,
  Eye,
  RefreshCw,
  LayoutTemplate,
} from 'lucide-react';
import api from '../../lib/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { GoogleEmbedPreview } from './GoogleEmbedPreview';
import { GoogleDocumentUpdateBanner } from './GoogleDocumentUpdateBanner';
import { GoogleCommentsImportModal } from './GoogleCommentsImportModal';
import { ImportBriefModal } from './ImportBriefModal';
import { ImportStoryboardModal } from './ImportStoryboardModal';
import { StoryboardSlidesPanel } from './StoryboardSlidesPanel';
import {
  type ExternalDocument,
  type GoogleDocType,
  docTypeLabel,
  googleUrlPlaceholder,
} from '../../lib/googleWorkspace';

interface GoogleWorkspaceJobPanelProps {
  jobId: string;
  readOnly?: boolean;
  invitationId?: string;
  scriptId?: string;
  showBrief?: boolean;
  showStoryboard?: boolean;
  showFolder?: boolean;
  showUpdateBanner?: boolean;
  className?: string;
  onBriefImported?: () => void;
  onStoryboardImported?: (scriptContent: string) => void;
  onCommentsImported?: () => void;
}

function DocIcon({ docType }: { docType: GoogleDocType }) {
  if (docType === 'presentation') return <Presentation className="h-4 w-4" />;
  if (docType === 'folder') return <FolderOpen className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

export function GoogleWorkspaceJobPanel({
  jobId,
  readOnly = false,
  invitationId,
  scriptId,
  showBrief = true,
  showStoryboard = true,
  showFolder = true,
  showUpdateBanner = true,
  className = '',
  onBriefImported,
  onStoryboardImported,
  onCommentsImported,
}: GoogleWorkspaceJobPanelProps) {
  const [documents, setDocuments] = useState<ExternalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState<GoogleDocType | null>(null);
  const [urlDrafts, setUrlDrafts] = useState<Record<string, string>>({});
  const [slideCounts, setSlideCounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [importBriefOpen, setImportBriefOpen] = useState(false);
  const [importStoryboardOpen, setImportStoryboardOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsDocId, setCommentsDocId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [applyingTemplates, setApplyingTemplates] = useState(false);
  const [watchBusy, setWatchBusy] = useState<string | null>(null);
  const [syncKey, setSyncKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/google-workspace/jobs/${jobId}/documents`);
      let docs: ExternalDocument[] = res.data.documents ?? [];
      if (invitationId) {
        docs = docs.filter(
          (d) =>
            d.docType === 'folder' ||
            (d.docType === 'document' && !d.invitationId) ||
            (d.docType === 'presentation' && d.invitationId === invitationId)
        );
      } else {
        docs = docs.filter((d) => !d.invitationId || d.docType === 'document' || d.docType === 'folder');
      }
      setDocuments(docs);
    } catch {
      setError('Failed to load linked Google files');
    } finally {
      setLoading(false);
    }
  }, [jobId, invitationId]);

  useEffect(() => {
    load();
  }, [load]);

  const findDoc = (docType: GoogleDocType) => {
    if (docType === 'presentation' && invitationId) {
      return documents.find((d) => d.docType === docType && d.invitationId === invitationId);
    }
    return documents.find((d) => d.docType === docType && !d.invitationId);
  };

  const handleLink = async (docType: GoogleDocType) => {
    const url = urlDrafts[docType]?.trim();
    if (!url) {
      setError('Paste a Google URL first');
      return;
    }
    setLinking(docType);
    setError(null);
    try {
      await api.post(`/google-workspace/jobs/${jobId}/documents`, {
        url,
        docType,
        invitationId: docType === 'presentation' ? invitationId : undefined,
        slideCount:
          docType === 'presentation' && slideCounts[docType]
            ? parseInt(slideCounts[docType], 10)
            : undefined,
      });
      setUrlDrafts((prev) => ({ ...prev, [docType]: '' }));
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to link file';
      setError(msg);
    } finally {
      setLinking(null);
    }
  };

  const handleUnlink = async (docId: string) => {
    if (!confirm('Remove this linked Google file from the campaign?')) return;
    try {
      await api.delete(`/google-workspace/jobs/${jobId}/documents/${docId}`);
      await load();
    } catch {
      setError('Failed to unlink file');
    }
  };

  const handleExportBrief = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.post<{ webViewLink: string; demo?: boolean }>(
        `/google-workspace/jobs/${jobId}/export-brief`
      );
      await load();
      if (res.data.webViewLink) {
        window.open(res.data.webViewLink, '_blank', 'noopener,noreferrer');
      }
      if (res.data.demo) {
        setError('Demo mode: export opened Google Docs create URL. Connect OAuth for automatic doc creation.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to export brief';
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleApplyTemplates = async () => {
    setApplyingTemplates(true);
    setError(null);
    try {
      await api.post(`/google-workspace/jobs/${jobId}/apply-templates`);
      await load();
      setSyncKey((k) => k + 1);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to apply templates';
      setError(msg);
    } finally {
      setApplyingTemplates(false);
    }
  };

  const briefDoc = findDoc('document');
  const storyboardDoc = findDoc('presentation');

  const handleWatchToggle = async (documentId: string, watch: boolean) => {
    setWatchBusy(documentId);
    try {
      await api.patch(`/google-workspace/jobs/${jobId}/documents/${documentId}/watch`, { watch });
      await load();
      setSyncKey((k) => k + 1);
    } catch {
      setError('Failed to update watch mode');
    } finally {
      setWatchBusy(null);
    }
  };

  const openComments = (documentId: string) => {
    setCommentsDocId(documentId);
    setCommentsOpen(true);
  };

  const sections: { docType: GoogleDocType; description: string; enabled: boolean }[] = [
    {
      docType: 'document',
      description: 'Link the brand’s campaign brief Google Doc for preview alongside structured fields.',
      enabled: showBrief,
    },
    {
      docType: 'presentation',
      description: invitationId
        ? 'Link a KOL-specific storyboard deck for brand and creator discussions.'
        : 'Link the master storyboard Google Slides deck for this campaign.',
      enabled: showStoryboard,
    },
    {
      docType: 'folder',
      description: 'Link a shared Drive folder for brief, storyboard, and deliverable files.',
      enabled: showFolder,
    },
  ];

  if (loading) {
    return (
      <Card className={`border-blue-200/50 ${className}`}>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-blue-200/60 bg-blue-50/20 dark:bg-blue-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Link2 className="h-5 w-5 text-blue-600" />
          Google Workspace
        </CardTitle>
        <CardDescription>
          Link Google Docs, Slides, and Drive folders — preview in kol.my without leaving the campaign.
          {showBrief && !readOnly && (
            <span className="block mt-1">
              Import brief fields from a linked Doc, or export the in-app brief to a new Google Doc.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {showUpdateBanner && showBrief && (
          <GoogleDocumentUpdateBanner
            key={syncKey}
            jobId={jobId}
            readOnly={readOnly}
            onReimportBrief={() => setImportBriefOpen(true)}
            onRefresh={() => setSyncKey((k) => k + 1)}
          />
        )}

        {showBrief && !readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!briefDoc || exporting}
              onClick={() => setImportBriefOpen(true)}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Import into brief
            </Button>
            <Button variant="outline" size="sm" disabled={exporting} onClick={() => void handleExportBrief()}>
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileOutput className="h-3.5 w-3.5 mr-1.5" />
              )}
              Export brief to Google Doc
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={applyingTemplates}
              onClick={() => void handleApplyTemplates()}
            >
              {applyingTemplates ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <LayoutTemplate className="h-3.5 w-3.5 mr-1.5" />
              )}
              Apply agency templates
            </Button>
          </div>
        )}

        {sections
          .filter((s) => s.enabled)
          .map(({ docType, description }) => {
            const linked = findDoc(docType);
            return (
              <div key={docType} className="rounded-lg border bg-white/60 dark:bg-black/20 p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <DocIcon docType={docType} />
                      {docTypeLabel(docType)}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{description}</p>
                  </div>
                  {linked && (
                    <Badge variant="outline" className="shrink-0">
                      Linked
                    </Badge>
                  )}
                </div>

                {linked ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{linked.title}</span>
                      {linked.slideCount != null && (
                        <span className="text-xs text-gray-500">{linked.slideCount} slides</span>
                      )}
                      <a
                        href={linked.webViewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                      >
                        Open in Google <ExternalLink className="h-3 w-3" />
                      </a>
                      {!readOnly && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto"
                          onClick={() => handleUnlink(linked.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Unlink
                        </Button>
                      )}
                    </div>
                    {linked.embedUrl && (
                      <GoogleEmbedPreview embedUrl={linked.embedUrl} title={linked.title || undefined} />
                    )}
                    {docType === 'folder' && !linked.embedUrl && (
                      <p className="text-sm text-gray-600">
                        Folder linked. Use Open in Google to browse files in Drive.
                      </p>
                    )}
                    {(docType === 'document' || docType === 'presentation') && linked && !readOnly && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openComments(linked.id)}
                        >
                          <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                          View comments
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={watchBusy === linked.id}
                          onClick={() =>
                            void handleWatchToggle(linked.id, linked.syncMode !== 'watch')
                          }
                        >
                          {watchBusy === linked.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          {linked.syncMode === 'watch' ? 'Watching for updates' : 'Watch for updates'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSyncKey((k) => k + 1)}>
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                          Check updates
                        </Button>
                      </div>
                    )}
                    {docType === 'presentation' && linked && !readOnly && scriptId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setImportStoryboardOpen(true)}
                      >
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        Import storyboard to script
                      </Button>
                    )}
                  </div>
                ) : (
                  !readOnly && (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder={googleUrlPlaceholder(docType)}
                        value={urlDrafts[docType] || ''}
                        onChange={(e) =>
                          setUrlDrafts((prev) => ({ ...prev, [docType]: e.target.value }))
                        }
                        className="flex-1"
                      />
                      {docType === 'presentation' && (
                        <Input
                          type="number"
                          placeholder="Slides (opt.)"
                          className="w-28"
                          value={slideCounts[docType] || ''}
                          onChange={(e) =>
                            setSlideCounts((prev) => ({ ...prev, [docType]: e.target.value }))
                          }
                        />
                      )}
                      <Button
                        size="sm"
                        data-help-id={docType === 'document' ? 'gws.link_button' : undefined}
                        onClick={() => handleLink(docType)}
                        disabled={linking === docType}
                      >
                        {linking === docType ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Link'
                        )}
                      </Button>
                    </div>
                  )
                )}

                {readOnly && !linked && (
                  <p className="text-sm text-gray-500">No {docTypeLabel(docType).toLowerCase()} linked yet.</p>
                )}
              </div>
            );
          })}
      </CardContent>

      {storyboardDoc && showStoryboard && scriptId && (
        <div className="px-6 pb-6">
          <StoryboardSlidesPanel
            jobId={jobId}
            documentId={storyboardDoc.id}
            scriptId={scriptId}
            readOnly={readOnly}
            onCommentsImported={onCommentsImported}
          />
        </div>
      )}

      {briefDoc && (
        <ImportBriefModal
          jobId={jobId}
          documentId={briefDoc.id}
          isOpen={importBriefOpen}
          onClose={() => setImportBriefOpen(false)}
          onApplied={() => {
            onBriefImported?.();
            setSyncKey((k) => k + 1);
          }}
        />
      )}

      {commentsDocId && (
        <GoogleCommentsImportModal
          jobId={jobId}
          documentId={commentsDocId}
          scriptId={scriptId}
          isOpen={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          onImported={() => {
            onCommentsImported?.();
            setSyncKey((k) => k + 1);
          }}
        />
      )}

      {storyboardDoc && scriptId && (
        <ImportStoryboardModal
          jobId={jobId}
          documentId={storyboardDoc.id}
          scriptId={scriptId}
          invitationId={invitationId}
          isOpen={importStoryboardOpen}
          onClose={() => setImportStoryboardOpen(false)}
          onApplied={(content) => onStoryboardImported?.(content)}
        />
      )}
    </Card>
  );
}
